// Universal form validation utilities for all AGO forms

export const formValidation = {
  // Common validation functions
  validatePhone: (phone) => {
    const phoneStr = phone.toString().trim();
    return /^\d{10}$/.test(phoneStr);
  },

  validatePincode: (pincode) => {
    const pincodeStr = pincode.toString().trim();
    return /^\d{6}$/.test(pincodeStr);
  },

  validateEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.toString().trim());
  },

  validatePassword: (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/;
    return passwordRegex.test(password);
  },

  validateAmount: (amount, minAmount = 0) => {
    const numAmount = parseFloat(amount);
    return !isNaN(numAmount) && numAmount >= minAmount;
  },

  // Investment form validation
  validateInvestmentForm: (formData) => {
    const errors = [];

    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!formData.phone || !formValidation.validatePhone(formData.phone)) {
      errors.push('Phone number must be exactly 10 digits');
    }
    if (!formData.email || !formValidation.validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (!formData.pincode || !formValidation.validatePincode(formData.pincode)) {
      errors.push('Pincode must be exactly 6 digits');
    }
    if (!formData.investmentAmount || !formValidation.validateAmount(formData.investmentAmount, 25000)) {
      errors.push('Minimum investment amount is ₹25,000');
    }
    // NEW: Required field validations
    if (!formData.investmentType || formData.investmentType.trim() === '') {
      errors.push('Investment type is required');
    }
    if (!formData.duration || formData.duration.trim() === '' || parseInt(formData.duration) < 1) {
      errors.push('Investment duration is required');
    }
    // Optional field validations
    if (formData.whatsapp && formData.whatsapp.trim() !== '' && !formValidation.validatePhone(formData.whatsapp)) {
      errors.push('WhatsApp number must be exactly 10 digits');
    }
    if (formData.age && formData.age.trim() !== '' && (parseInt(formData.age) < 18 || parseInt(formData.age) > 100)) {
      errors.push('Age must be between 18 and 100');
    }

    return errors;
  },

  // Contact form validation
  validateContactForm: (formData) => {
    const errors = [];

    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!formData.email || !formValidation.validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (!formData.phone || !formValidation.validatePhone(formData.phone)) {
      errors.push('Phone number must be exactly 10 digits');
    }
    if (!formData.subject || formData.subject.trim() === '') {
      errors.push('Subject is required');
    }
    if (!formData.message || formData.message.trim() === '') {
      errors.push('Message is required');
    }

    return errors;
  },

  // Workshop form validation
  validateWorkshopForm: (formData) => {
    const errors = [];

    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!formData.address || formData.address.trim() === '') {
      errors.push('Address is required');
    }
    if (!formData.phone || !formValidation.validatePhone(formData.phone)) {
      errors.push('Phone number must be exactly 10 digits');
    }
    if (!formData.email || !formValidation.validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (formData.whatsapp && formData.whatsapp.trim() !== '' && !formValidation.validatePhone(formData.whatsapp)) {
      errors.push('WhatsApp number must be exactly 10 digits');
    }
    if (formData.completeOnTime !== 'yes') {
      errors.push('You must commit to completing the work on time');
    }
    if (!formData.rulesAccepted) {
      errors.push('You must accept the rules and regulations');
    }

    return errors;
  },

  // Subscription registration validation
  validateSubscriptionForm: (formData) => {
    const errors = [];

    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!formData.phone || !formValidation.validatePhone(formData.phone)) {
      errors.push('Phone number must be exactly 10 digits');
    }
    if (!formData.email || !formValidation.validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (!formData.address || formData.address.trim() === '') {
      errors.push('Address is required');
    }
    if (!formData.pincode || !formValidation.validatePincode(formData.pincode)) {
      errors.push('Pincode must be exactly 6 digits');
    }
    if (!formData.age || isNaN(parseInt(formData.age)) || parseInt(formData.age) < 13 || parseInt(formData.age) > 100) {
      errors.push('Age must be between 13 and 100');
    }
    if (!formData.password || !formValidation.validatePassword(formData.password)) {
      errors.push('Password must be 8-16 characters with uppercase, lowercase, number, and special character');
    }
    if (!formData.studentCategory || formData.studentCategory.trim() === '') {
      errors.push('Student category is required');
    }

    return errors;
  },

  // Customer work form validation
  validateCustomerWorkForm: (formData) => {
    const errors = [];

    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!formData.address || formData.address.trim() === '') {
      errors.push('Address is required');
    }
    if (!formData.phone || !formValidation.validatePhone(formData.phone)) {
      errors.push('Phone number must be exactly 10 digits');
    }
    if (!formData.email || !formValidation.validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (formData.whatsapp && formData.whatsapp.trim() !== '' && !formValidation.validatePhone(formData.whatsapp)) {
      errors.push('WhatsApp number must be exactly 10 digits');
    }
    if (!formData.workType || !['assignment', 'journal', 'project', 'thesis', 'other'].includes(formData.workType)) {
      errors.push('Please select a valid work type');
    }
    if (formData.workType === 'assignment' && (!formData.pages || parseInt(formData.pages) < 1)) {
      errors.push('Number of pages must be at least 1');
    }
    if (['journal', 'project', 'thesis', 'other'].includes(formData.workType) && (!formData.amount || parseFloat(formData.amount) < 1)) {
      errors.push('Amount must be at least ₹1');
    }
    if (!formData.deadline || new Date(formData.deadline) <= new Date()) {
      errors.push('Deadline must be in the future');
    }
    if (!formData.agreementAccepted) {
      errors.push('You must accept the agreement');
    }
    if (formData.workType === 'other' && (!formData.customWorkType || formData.customWorkType.trim() === '')) {
      errors.push('Please specify the work type when selecting "Other"');
    }

    return errors;
  },

  // Data formatting functions
  formatInvestmentData: (formData) => {
    const cleanData = {
      name: formData.name.toString().trim(),
      address: formData.address.toString().trim(), // Include address field
      pincode: formData.pincode.toString().trim(),
      phone: formData.phone.toString().trim(),
      email: formData.email.toString().trim().toLowerCase(),
      investmentAmount: formData.investmentAmount.toString().trim(),
      // NEW: Required fields
      investmentType: formData.investmentType.toString().trim(), // REQUIRED
      duration: formData.duration.toString().trim() // REQUIRED
    };

    // Add optional fields only if they have values
    if (formData.occupation && formData.occupation.trim()) {
      cleanData.occupation = formData.occupation.toString().trim();
    }
    if (formData.whatsapp && formData.whatsapp.trim()) {
      cleanData.whatsapp = formData.whatsapp.toString().trim();
    }
    if (formData.age && formData.age.trim()) {
      cleanData.age = formData.age.toString().trim(); // Age
    }
    if (formData.paymentFrequency && formData.paymentFrequency.trim()) {
      cleanData.paymentFrequency = formData.paymentFrequency.toString().trim();
    }

    return cleanData;
  },

  formatContactData: (formData) => {
    return {
      name: formData.name.toString().trim(),
      email: formData.email.toString().trim().toLowerCase(),
      phone: formData.phone.toString().trim(),
      subject: formData.subject.toString().trim(),
      message: formData.message.toString().trim()
    };
  },

  formatWorkshopData: (formData) => {
    return {
      workItemId: formData.workItemId,
      name: formData.name.toString().trim(),
      address: formData.address.toString().trim(),
      phone: formData.phone.toString().trim(),
      whatsapp: formData.whatsapp ? formData.whatsapp.toString().trim() : '',
      email: formData.email.toString().trim().toLowerCase(),
      completeOnTime: formData.completeOnTime.toString(),
      rulesAccepted: Boolean(formData.rulesAccepted)
    };
  },

  formatSubscriptionData: (formData) => {
    return {
      name: formData.name.toString().trim(),
      phone: formData.phone.toString().trim(),
      address: formData.address.toString().trim(),
      pincode: formData.pincode.toString().trim(),
      age: parseInt(formData.age),
      email: formData.email.toString().trim().toLowerCase(),
      password: formData.password.toString(),
      studentCategory: formData.studentCategory.toString().trim()
    };
  },

  formatCustomerWorkData: (formData) => {
    const formatted = {
      name: formData.name.toString().trim(),
      address: formData.address.toString().trim(),
      phone: formData.phone.toString().trim(),
      whatsapp: formData.whatsapp ? formData.whatsapp.toString().trim() : '',
      email: formData.email.toString().trim().toLowerCase(),
      workType: formData.workType.toString(),
      deadline: formData.deadline.toString(),
      additionalInfo: formData.additionalInfo ? formData.additionalInfo.toString().trim() : '',
      agreementAccepted: Boolean(formData.agreementAccepted)
    };

    // Add optional fields
    if (formData.occupation && formData.occupation.trim()) {
      formatted.occupation = formData.occupation.toString().trim(); // NEW: Occupation
    }
    if (formData.customWorkType && formData.customWorkType.trim()) {
      formatted.customWorkType = formData.customWorkType.toString().trim(); // NEW: Custom work type
    }

    // Add conditional fields based on work type
    if (formData.workType === 'assignment') {
      formatted.pages = parseInt(formData.pages);
    } else if (['journal', 'project', 'thesis', 'other'].includes(formData.workType)) {
      formatted.amount = parseFloat(formData.amount);
    }

    return formatted;
  }
};

export default formValidation;